import { Link, createFileRoute, getRouteApi } from "@tanstack/react-router";
import { SponsoredJobsSection } from "@/components/common/jobs/sponsoredJobsList";
import Hero from "@/components/hero";
import JobCard from "@/components/common/jobs/jobCard";
import BreadcrumbJsonLd from "@/components/common/breadcrumbJsonLd";
import { getJobs, getJobsCount } from "@/lib/api";
import { jsonLdSafe } from "@/lib/jsonLd";
import type { JobWithRelations, PaginatedJobs } from "@/types/db";

const SITE_URL = "https://datafirstjobs.com";
const rootRoute = getRouteApi("__root__");

// Pull more recent jobs than we show so we can filter out the sponsored
// duplicates and still have six fresh ones.
const RECENT_FETCH_LIMIT = 18;
const RECENT_DISPLAY_LIMIT = 6;
const FRESHNESS_WINDOW_DAYS = 7;

function sevenDaysAgoIso(): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - FRESHNESS_WINDOW_DAYS);
  // API accepts ISO-8601; strip fractional millis for a stable cache-ish key.
  return d.toISOString().replace(/\.\d+Z$/, "Z");
}

// Tailwind needs these classes present in source so JIT emits them; picking
// via a tiny helper keeps the grid count in sync with visible stats.
function gridColsForStatCount(count: number): string {
  if (count >= 3) return "grid-cols-3";
  if (count === 2) return "grid-cols-2";
  return "grid-cols-1";
}

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      {
        title: "Data First Jobs | Data Engineering & Analytics Jobs",
      },
      {
        name: "description",
        content:
          "Browse jobs for data professionals by tech stack, skills, titles, and more. No more digging through countless filters for the right job.",
      },
      {
        property: "og:title",
        content: "Data First Jobs | Data Engineering & Analytics Jobs",
      },
      {
        property: "og:description",
        content:
          "Browse jobs for data professionals by tech stack, skills, titles, and more.",
      },
      { property: "og:url", content: SITE_URL },
    ],
    links: [{ rel: "canonical", href: SITE_URL }],
  }),
  loader: async () => {
    // Sponsored jobs are essential — everything else is nice-to-have and
    // is caught to null so a single outage can't blank the homepage.
    const createdAfter = sevenDaysAgoIso();
    const [sponsored, jobsCount, recent, newThisWeek] = await Promise.all([
      getJobs({
        data: { limit: 10, sponsored: true },
      }) as Promise<PaginatedJobs>,
      getJobsCount({ data: {} }).catch(() => null),
      // Explicitly exclude sponsored — the "Fresh data jobs" grid sits below
      // the sponsored rail, so same-slug overlap (and sponsored cards
      // beyond the first 10 that would otherwise slip through) need to be
      // excluded at the source, not just filtered client-side.
      (
        getJobs({
          data: { limit: RECENT_FETCH_LIMIT, sponsored: false },
        }) as Promise<PaginatedJobs>
      ).catch(() => null),
      getJobsCount({ data: { createdAfter } }).catch(() => null),
    ]);

    return {
      jobs: sponsored.jobs,
      totalJobs: jobsCount?.total ?? null,
      recentJobs: (recent?.jobs ?? []) as JobWithRelations[],
      newThisWeek: newThisWeek?.total ?? null,
    };
  },
  component: Home,
});

function Home() {
  const { jobs, totalJobs, recentJobs, newThisWeek } = Route.useLoaderData();
  const { jobCategoriesWithTags: jobCategories } = rootRoute.useRouteContext();

  // Drop any recent job that's already rendered in the sponsored rail so
  // we don't show the same card twice on the same page.
  const sponsoredSlugs = new Set(jobs.map((j) => j.slug));
  const freshRecent = recentJobs
    .filter((j) => j.slug && !sponsoredSlugs.has(j.slug))
    .slice(0, RECENT_DISPLAY_LIMIT);

  const stats: { value: string; label: string }[] = [];
  if (totalJobs != null) {
    stats.push({ value: totalJobs.toLocaleString(), label: "Open roles" });
  }
  if (newThisWeek != null) {
    stats.push({
      value: newThisWeek.toLocaleString(),
      label: "New this week",
    });
  }
  stats.push({ value: String(jobCategories.length), label: "Categories" });

  return (
    <>
      <BreadcrumbJsonLd items={[{ name: "Home" }]} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: jsonLdSafe({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Data First Jobs",
            url: SITE_URL,
            logo: `${SITE_URL}/og-default.png`,
            sameAs: [],
            description:
              "Browse jobs for data professionals by tech stack, skills, titles, and more.",
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: jsonLdSafe({
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "Data First Jobs",
            url: SITE_URL,
            potentialAction: {
              "@type": "SearchAction",
              target: {
                "@type": "EntryPoint",
                urlTemplate: `${SITE_URL}/search?q={search_term_string}`,
              },
              "query-input": "required name=search_term_string",
            },
          }),
        }}
      />

      <Hero stats={{ jobs: totalJobs }} />

      {/* Scale + credibility. Track the visible stat count so the grid
          collapses cleanly on weeks where velocity is zero (middle cell
          hides; we don't want a blank third column). */}
      <section
        aria-label="Site stats"
        className="mx-auto mt-20 w-full max-w-5xl px-4 sm:px-6 lg:px-8"
      >
        <dl
          className={`grid ${gridColsForStatCount(stats.length)} gap-4 rounded-2xl border border-gray-100 bg-white/60 p-6 text-center shadow-sm backdrop-blur-sm sm:gap-8 sm:p-8`}
        >
          {stats.map((stat) => (
            <div key={stat.label}>
              <dt className="order-last mt-2 text-xs font-medium tracking-wide text-gray-500 uppercase sm:text-sm sm:tracking-normal sm:normal-case">
                {stat.label}
              </dt>
              <dd className="text-polaris-orange text-3xl font-semibold tracking-tight tabular-nums sm:text-4xl lg:text-5xl">
                {stat.value}
              </dd>
            </div>
          ))}
        </dl>
      </section>

      {/* Curated — paid placement */}
      <SponsoredJobsSection jobs={jobs} heading="Hand-picked this week" />

      {/* Fresh — last N days */}
      {freshRecent.length > 0 && (
        <section
          aria-labelledby="latest-jobs-heading"
          className="mx-auto mt-20 w-full max-w-5xl px-4 sm:px-6 lg:px-8"
        >
          <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="mb-1 font-mono text-xs tracking-wide text-gray-500 uppercase">
                Latest
              </p>
              <h2
                id="latest-jobs-heading"
                className="text-xl font-semibold tracking-tight text-gray-900 sm:text-2xl"
              >
                Fresh data jobs
              </h2>
            </div>
            <Link
              to="/search"
              className="text-sm font-medium text-gray-700 transition-colors hover:text-gray-900"
            >
              {totalJobs != null
                ? `See all ${totalJobs.toLocaleString()} jobs`
                : "See all jobs"}{" "}
              <span aria-hidden="true">→</span>
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {freshRecent.map((job) => (
              <JobCard key={job.id} job={job} variant="compact" />
            ))}
          </div>
          <div className="mt-8 flex justify-center">
            <Link
              to="/search"
              className="bg-polaris-orange inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-orange-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 sm:text-base"
            >
              Browse every open role
              <span aria-hidden="true">→</span>
            </Link>
          </div>
        </section>
      )}
    </>
  );
}
